"use client";
import { useState } from "react";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { CarouselContent } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { BannersData, WebBanner } from "@/types/api/home";
import Image from "next/image";
import { useCarousel } from "@/hooks/utility/useCarousel";
import Link from "next/link";
import { useAuth } from "@/hooks/auth/useAuth";
import { leadFlow } from "@/components/shared/modals/flows";
import { ModalFlow } from "@/types/components/modal";
import { usePathname } from "next/navigation";
import { FORM_TYPES, getBannerLeadCallout } from "@/lib/constants";
import Autoplay from "embla-carousel-autoplay";

const DynamicModal = dynamic(
  () => import("@/components/shared/modals/DynamicModal"),
  { ssr: false }
);

function hasBannerRedirect(url?: string | null) {
  const trimmed = url?.trim();
  return Boolean(trimmed && trimmed !== "#");
}

function BannerCarousel({
  banners,
  itemClassName = "",
  onOpenLead,
}: {
  banners: WebBanner[];
  itemClassName?: string;
  onOpenLead: () => void;
}) {
  const { currentIndex, carouselApi, setCarouselApi } = useCarousel();

  return (
    <div className="relative">
      <Carousel
        opts={{ loop: true, skipSnaps: true, watchDrag: true }}
        plugins={
          banners.length > 1
            ? [Autoplay({ delay: 4000, stopOnInteraction: false })]
            : undefined
        }
        setApi={setCarouselApi}
        className="w-full">
        <CarouselContent>
          {banners.map((item: WebBanner, index: number) => {
            const canRedirect = hasBannerRedirect(item.redirect_url);
            const image = (
              <Image
                src={item.image}
                alt="Banner image"
                fill
                quality={85}
                loading="lazy"
                className="rounded-xl object-cover object-left"
              />
            );

            return (
              <CarouselItem key={index}>
                <div
                  className={cn(
                    // mobile 343×166 (~2.07:1), desktop 1216×400 (~3.04:1)
                    "relative aspect-[343/166] w-full overflow-hidden rounded-xl md:aspect-[1216/400]",
                    itemClassName
                  )}>
                  {canRedirect ? (
                    <Link
                      href={item.redirect_url}
                      className="absolute inset-0 block">
                      {image}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={onOpenLead}
                      className="absolute inset-0 block cursor-pointer">
                      {image}
                    </button>
                  )}
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        {(carouselApi?.canScrollPrev() || carouselApi?.canScrollNext()) && (
          <>
            <CarouselPrevious className="absolute top-1/2 left-4 hidden h-12 w-12 -translate-y-1/2 cursor-pointer bg-black/40 text-white transition-opacity duration-300 md:flex" />
            <CarouselNext className="absolute top-1/2 right-4 hidden h-12 w-12 -translate-y-1/2 cursor-pointer bg-black/40 text-white transition-opacity duration-300 md:flex" />
          </>
        )}
        {banners.length > 1 && (
          <div className="absolute bottom-0 flex w-full transform gap-2 p-4">
            <div className="flex w-full items-center justify-center gap-1">
              {banners.map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-2 w-2 rounded-full border border-white bg-gray-300 md:h-2.5 md:w-2.5",
                    index === currentIndex ? "bg-white" : "bg-transparent"
                  )}
                  onClick={() => carouselApi?.scrollTo(index)}></Button>
              ))}
            </div>
          </div>
        )}
      </Carousel>
    </div>
  );
}

export default function Banners({
  banners,
  bgColor,
  containerClassName = "",
  itemClassName = "",
}: BannersData & {
  bgColor?: string;
  containerClassName?: string;
  itemClassName?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flow, setFlow] = useState<ModalFlow | null>(null);
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();

  const handleOpenLead = async () => {
    try {
      const modalFlow = await leadFlow(isAuthenticated);
      setFlow(modalFlow);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to load flow:", error);
    }
  };

  if (banners.web_banners.length === 0 && banners.mweb_banners.length === 0)
    return null;
  return (
    <div className={bgColor}>
      <div className={cn("py-4 md:py-10", containerClassName)}>
        <div className="hidden md:block">
          <BannerCarousel
            banners={banners.web_banners}
            itemClassName={itemClassName}
            onOpenLead={handleOpenLead}
          />
        </div>
        <div className="block md:hidden">
          <BannerCarousel
            banners={banners.mweb_banners}
            itemClassName={itemClassName}
            onOpenLead={handleOpenLead}
          />
        </div>
      </div>
      {isModalOpen && flow && (
        <DynamicModal
          flow={flow}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          metadata={{
            form_name: FORM_TYPES.LEAD_WALKTHROUGH_FORM,
            page_url: pathname,
            referring_callout: getBannerLeadCallout(pathname),
          }}
        />
      )}
    </div>
  );
}
